import {
  Colors,
  LoadingIndicator,
  SaveFormTop,
  SearchInput,
  tokens,
  TopModalBar,
  useGetDimensions,
} from '@gripp/shared-ui';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import '../../style/reactTabStyle.css';

/** The selection change handed to a tab's `onSave` on submit. */
export type AssignmentChange = {
  /** The full current selection. Use this for "replace with full set" mutations. */
  selected: string[];
  /** Ids selected now but not originally. Use with `removed` for diff mutations. */
  added: string[];
  /** Ids originally assigned but now unselected. */
  removed: string[];
};

export type AssignmentTab = {
  /** Stable key; also used to keep per-tab selection state apart. */
  key: string;
  /** Already-translated tab label. */
  label: string;
  /** Already-translated search placeholder shown while this tab is active. */
  searchPlaceholder: string;
  /**
   * The originally-assigned ids for this tab (the diff/dirty baseline).
   * `undefined` while the parent is still loading this tab's data — the list is
   * withheld until it resolves.
   */
  assignments: string[] | undefined;
  /** Persist this tab. Receives the computed diff; the parent owns the mutation + cache invalidation. */
  onSave: (change: AssignmentChange) => Promise<void>;
  /**
   * Render the tab's list. The modal owns the working selection and passes it
   * down, so any existing `*List` (which renders `AssignmentListGrid`) drops in
   * unchanged — just forward `assignments`/`onAssignmentsChange`.
   */
  renderList: (props: {
    keyword: string;
    maxHeight: number;
    assignments: string[];
    onAssignmentsChange: (ids: string[]) => void;
  }) => ReactNode;
};

export type AssignmentModalProps = {
  title: string;
  /** One entry per tab. A single entry renders as a plain (tab-less) list. */
  tabs: AssignmentTab[];
  /** Which tab to open on first render (defaults to the first). */
  initialTabKey?: string;
  /** Force-show/hide the tab bar (defaults to showing it only when >1 tab). */
  showTabs?: boolean;
  /** Parent's initial-assignments load still pending. */
  isLoading?: boolean;
  onComplete: () => void;
};

const areSameSet = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};

/**
 * The shared "assign X to Y" modal shell. Owns everything every assignment
 * editor used to duplicate — the search box, per-tab working selection seeded
 * from each tab's originals, dirty tracking, the `SaveFormTop` chrome, layout
 * sizing, and diff computation on submit — and delegates only the genuinely
 * per-entity concerns (initial load, save mutation, and the list itself) to the
 * caller via {@link AssignmentTab}. Selection is preserved across search/sort
 * (see `AssignmentListGrid`) and across tab switches.
 */
export const AssignmentModal = ({
  title,
  tabs,
  initialTabKey,
  showTabs,
  isLoading = false,
  onComplete,
}: AssignmentModalProps) => {
  const { dimensions, onLayout } = useGetDimensions();
  const listMaxHeight = (dimensions?.height ?? 0) - 50;
  const tabsVisible = showTabs ?? tabs.length > 1;

  const [activeIndex, setActiveIndex] = useState(() => {
    const i = initialTabKey
      ? tabs.findIndex((tab) => tab.key === initialTabKey)
      : 0;
    return i >= 0 ? i : 0;
  });
  const [keyword, setKeyword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Working selection per tab. Seeded from each tab's `assignments` on (re)load.
  const [selectionByKey, setSelectionByKey] = useState<
    Record<string, string[]>
  >({});

  // Re-open the requested tab when the caller changes it (e.g. a different
  // entry-point button on the same entity).
  useEffect(() => {
    if (!initialTabKey) return;
    const i = tabs.findIndex((tab) => tab.key === initialTabKey);
    if (i >= 0) setActiveIndex(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTabKey]);

  // Reset the shared search box when switching tabs.
  useEffect(() => {
    setKeyword('');
  }, [activeIndex]);

  // Seed (and re-seed) the working copy from a tab's originals whenever they
  // resolve or genuinely change value — never on mere array-identity churn, so
  // in-progress edits are not clobbered by parent re-renders.
  const seededByKey = useRef<Record<string, string[]>>({});
  useEffect(() => {
    let next: Record<string, string[]> | undefined;
    tabs.forEach((tab) => {
      if (!tab.assignments) return;
      const seeded = seededByKey.current[tab.key];
      if (seeded && areSameSet(seeded, tab.assignments)) return;
      seededByKey.current[tab.key] = tab.assignments;
      next = { ...(next ?? selectionByKey), [tab.key]: tab.assignments };
    });
    if (next) setSelectionByKey(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  const active = tabs[activeIndex] ?? tabs[0];
  const activeSelection = useMemo(
    () => selectionByKey[active.key] ?? active.assignments ?? [],
    [selectionByKey, active.key, active.assignments]
  );

  const isDirty = useMemo(() => {
    if (!active.assignments) return false;
    return !areSameSet(active.assignments, activeSelection);
  }, [active.assignments, activeSelection]);

  const handleSubmit = async () => {
    const original = active.assignments ?? [];
    const selected = activeSelection;
    const change: AssignmentChange = {
      selected,
      added: selected.filter((id) => !original.includes(id)),
      removed: original.filter((id) => !selected.includes(id)),
    };
    setSubmitting(true);
    try {
      await active.onSave(change);
      onComplete();
    } finally {
      setSubmitting(false);
    }
  };

  const setTabSelection = (key: string) => (ids: string[]) =>
    setSelectionByKey((prev) => ({ ...prev, [key]: ids }));

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={!isLoading && !submitting && isDirty}
    >
      {isLoading || submitting ? (
        <LoadingIndicator flexContainer />
      ) : (
        <View style={styles.container}>
          <SearchInput
            value={keyword}
            onChangeText={setKeyword}
            onDebounce={setKeyword}
            debounceLength={3}
            backgroundColor={Colors.white}
            placeholder={active.searchPlaceholder}
            height={44}
          />
          <View style={styles.assignmentContainer} onLayout={onLayout}>
            <Tabs
              selectedIndex={activeIndex}
              onSelect={(index) => setActiveIndex(index)}
            >
              <TabList style={{ display: tabsVisible ? 'flex' : 'none' }}>
                {tabs.map((tab) => (
                  <Tab key={tab.key}>{tab.label}</Tab>
                ))}
              </TabList>
              <View style={styles.listContainer}>
                {tabs.map((tab) => (
                  <TabPanel key={tab.key}>
                    {tab.assignments &&
                      tab.renderList({
                        keyword,
                        maxHeight: listMaxHeight,
                        assignments:
                          selectionByKey[tab.key] ?? tab.assignments,
                        onAssignmentsChange: setTabSelection(tab.key),
                      })}
                  </TabPanel>
                ))}
              </View>
            </Tabs>
          </View>
        </View>
      )}
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: tokens.spacing.xl,
    paddingTop: tokens.spacing['2xl'],
    paddingBottom: tokens.spacing.xl,
  },
  assignmentContainer: { flex: 1, marginTop: tokens.spacing.xl },
  listContainer: {
    borderTopRightRadius: tokens.borderRadius.xl,
    borderBottomRightRadius: tokens.borderRadius.xl,
    borderBottomLeftRadius: tokens.borderRadius.xl,
    overflow: 'hidden',
  },
});
